import { BotPlatform, BotSessionResponse, BotSessionStatus } from '../../../services/api.service';

/** Простая функция перевода — совместима с `t` из useTranslation() без завязки на
 *  конкретную версию типов react-i18next. */
type TFn = (key: string, options?: Record<string, unknown>) => string;

const PLATFORM_HOST_SUFFIXES: Record<BotPlatform, string[]> = {
  zoom: ['zoom.us'],
  teams: ['teams.microsoft.com', 'teams.live.com'],
  meet: ['meet.google.com'],
};

function hostMatchesSuffix(hostname: string, suffix: string): boolean {
  const host = hostname.toLowerCase();
  const target = suffix.toLowerCase();
  return host === target || host.endsWith(`.${target}`);
}

export type MeetingUrlCheck = 'empty' | 'valid' | 'unknown-domain' | 'not-a-url';

/** Мягкая проверка ссылки по платформе — только для подсказки, никогда не блокирует отправку:
 *  у заказчиков бывают кастомные корпоративные домены Zoom/Teams/Meet. */
export function checkMeetingUrl(platform: BotPlatform, rawUrl: string): MeetingUrlCheck {
  const trimmed = rawUrl.trim();
  if (!trimmed) return 'empty';

  let hostname = '';
  try {
    const withProto = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    hostname = new URL(withProto).hostname;
  } catch {
    return 'not-a-url';
  }
  if (!hostname) return 'not-a-url';

  const matches = PLATFORM_HOST_SUFFIXES[platform].some((suffix) => hostMatchesSuffix(hostname, suffix));
  return matches ? 'valid' : 'unknown-domain';
}

/** "processing" — это фактический терминальный статус успеха на сегодня: bots.py доводит его
 *  только до "processing" (см. submit_bot_recording), "done"/"completed" в коде не встречаются,
 *  но обрабатываем их тоже на случай, если бэкенд начнёт слать финальный статус. */
export function isBotSessionTerminal(status: BotSessionStatus): boolean {
  return status === 'processing' || status === 'done' || status === 'completed' || status === 'failed';
}

export function isBotSessionFailed(status: BotSessionStatus): boolean {
  return status === 'failed';
}

export function formatScheduledTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Человекочитаемый статус бот-сессии — используется и в статус-экране, и в списке
 *  последних сессий под формой. */
export function botSessionStatusText(
  t: TFn,
  session: Pick<BotSessionResponse, 'status' | 'scheduled_at' | 'error'>,
): string {
  switch (session.status) {
    case 'scheduled':
      return session.scheduled_at
        ? t('bot-connect.status-scheduled-at', { time: formatScheduledTime(session.scheduled_at) })
        : t('bot-connect.status-scheduled');
    case 'joining':
      return t('bot-connect.status-joining');
    case 'recording':
      return t('bot-connect.status-recording');
    case 'uploading':
      return t('bot-connect.status-uploading');
    case 'processing':
    case 'done':
    case 'completed':
      return t('bot-connect.status-done');
    case 'failed':
      return t('bot-connect.status-failed', {
        error: session.error || t('bot-connect.status-failed-unknown'),
      });
    default:
      return session.status;
  }
}

/** Добавляет/обновляет сессию в списке «последних», новые сверху, без дублей по id. */
export function upsertBotSession(
  list: BotSessionResponse[],
  updated: BotSessionResponse,
  limit = 5,
): BotSessionResponse[] {
  return [updated, ...list.filter((item) => item.id !== updated.id)].slice(0, limit);
}
